import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet, Link } from 'react-router-dom';

import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useCan } from '../../hooks/useCan';
import { workspaceAPI } from '../../lib/api';
import SectionSwitcher from './SectionSwitcher';

/**
 * The Fleet section's frame: ToolsLayout's bar, on theme tokens, inside a
 * theme container so the operator's light/dark and standard/modern choices
 * apply here as they do in the rundown editor. Fleet belongs to the account,
 * so the workspace switcher only changes which account's boxes are shown.
 *
 * Scoring uses the same frame: `section` is the switcher's entry, and
 * `capability` and `denied` what it takes to open and what to say otherwise.
 */
export default function FleetLayout({ section = 'fleet', capability = 'fleet:operate', denied = null }) {
  const { user, setAuth, logout } = useAuthStore();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const uiStyle = useSettingsStore((s) => s.uiStyle);
  const canOperate = useCan(capability);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false);
  const wsDropdownRef = useRef(null);

  const loadWorkspaces = useCallback(() => {
    workspaceAPI.mine().then((r) => setAllWorkspaces(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  useEffect(() => {
    function handleClick(e) {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target)) setWsDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSwitchWorkspace(workspaceId) {
    try {
      const res = await workspaceAPI.switch(workspaceId);
      setAuth(res.data.token, res.data.user);
      setWsDropdownOpen(false);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to switch workspace');
    }
  }

  return (
    <div className={`${theme === 'light' ? 'theme-light' : 'theme-dark'} style-${uiStyle} min-h-screen bg-app-canvas text-app-fg`}>
      <nav className="bg-app-chrome border-b border-app-line-hard px-4 sm:px-6 h-12 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center flex-shrink-0">
            <div className="w-2.5 h-2.5 bg-white rounded-full" />
          </div>
          <span className="hidden sm:inline font-medium text-app-fg text-sm">Mission Control</span>

          <div className="relative min-w-0" ref={wsDropdownRef}>
            <button
              onClick={() => setWsDropdownOpen((v) => !v)}
              className="flex items-center gap-1.5 text-gray-400 hover:text-app-fg text-sm transition-colors min-w-0"
            >
              <span className="text-gray-600">/</span>
              <span className="truncate">{user?.workspace_name || 'Workspace'}</span>
              {allWorkspaces.length > 1 && (
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${wsDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {wsDropdownOpen && allWorkspaces.length > 1 && (
              <div className="absolute left-0 top-full mt-1 w-56 bg-app-panel border border-app-line-chrome rounded-lg shadow-xl z-50 py-1">
                {allWorkspaces.map((ws) => (
                  <button
                    key={ws.id}
                    onClick={() => handleSwitchWorkspace(ws.id)}
                    disabled={ws.active}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      ws.active ? 'text-app-fg bg-app-row-hover cursor-default' : 'text-gray-400 hover:text-app-fg hover:bg-app-row-hover'
                    }`}
                  >
                    {ws.active ? <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" /> : <span className="w-1.5 h-1.5 flex-shrink-0" />}
                    <span className="truncate">{ws.name}</span>
                    <span className="ml-auto text-xs text-gray-600 capitalize">{ws.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <SectionSwitcher active={section} />
        </div>

        <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
          <button
            onClick={toggleTheme}
            title={theme === 'light' ? 'Dark theme' : 'Light theme'}
            className="text-gray-500 hover:text-app-fg transition-colors"
          >
            {theme === 'light' ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            )}
          </button>
          {user?.avatar_url && <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />}
          <span className="hidden md:inline text-gray-500 text-sm">{user?.name || user?.email}</span>
          <button onClick={logout} className="text-gray-500 hover:text-app-fg text-sm transition-colors">Sign Out</button>
        </div>
      </nav>

      {canOperate ? <Outlet /> : denied || (
        <div className="max-w-xl mx-auto px-6 py-16 text-center">
          <h1 className="text-lg font-semibold text-app-fg">Fleet</h1>
          <p className="text-sm text-gray-400 mt-2">
            The Fleet shows the CamNode boxes that belong to an account. It is open to the account&apos;s members; you are
            not a member of the account that owns this workspace.
          </p>
          <Link to="/" className="inline-block mt-4 text-sm text-blue-500 hover:text-blue-400">Back to shows</Link>
        </div>
      )}
    </div>
  );
}
